import { FC, useEffect } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { observer } from 'mobx-react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import styles from './index.module.scss'
import { ICompanyReferenceBase, MessageType } from '../../../types'
import { getQueryValue, useStores } from '../../../utils'
import { TextAreaGroup } from '../TextAreaGroup'
import { SettingButton } from '../../atoms/SettingButton'

type Props = {
  reference?: ICompanyReferenceBase
  references: ICompanyReferenceBase[]
  setReferences: (references: ICompanyReferenceBase[]) => void
  setEditingReference: (reference: ICompanyReferenceBase) => void
}

type Value = {
  comment: string
}

export const CompanyReferenceForm: FC<Props> = observer(
  ({ reference, references, setReferences, setEditingReference }) => {
    const { t } = useTranslation(['p', 'validation'])
    const { messages, viewer } = useStores()
    const router = useRouter()
    const companySlug = getQueryValue('id', router)
    const initialValue: Value = {
      comment: '',
    }

    const validationSchema = Yup.object().shape({
      comment: Yup.string()
        .min(20, t('validation:20文字以上で入力してください'))
        .max(300, t('validation:300文字以内で入力してください'))
        .required(t('validation:必須項目です')),
    })

    return (
      <div className={styles.container}>
        <Formik
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={async (values: Value, actions) => {
            if (reference) {
              const newReference = await viewer.viewer?.updateCompanyReference(reference.id, values.comment)
              actions.setSubmitting(false)
              if (newReference) {
                const newReferences = references.map((r) => {
                  if (r.id === newReference.id) {
                    return newReference
                  }
                  return r
                })
                setReferences(newReferences)
                setEditingReference(null)
                messages.add({
                  type: MessageType.Info,
                  body: t('応援コメントを変更しました'),
                  isTranslated: true,
                  ttl: 5000,
                })
                actions.resetForm()
              }
            } else {
              const newReference = await viewer.viewer?.addCompanyReference(companySlug, values.comment)
              actions.setSubmitting(false)
              if (newReference) {
                setReferences(references.concat(newReference))
                messages.add({
                  type: MessageType.Info,
                  body: t('応援コメントを投稿しました'),
                  isTranslated: true,
                  ttl: 5000,
                })
              }
              actions.resetForm()
            }
          }}
        >
          {({ isSubmitting, setFieldValue }) => {
            useEffect(() => {
              if (!reference) {
                return
              }

              setFieldValue('comment', reference?.comment)
            }, [reference])

            return (
              <Form>
                <div className={styles.textarea}>
                  <Field
                    name='comment'
                    placeholder={t('応援コメントを投稿しましょう')}
                    fieldType='setting'
                    isSmall
                    component={TextAreaGroup}
                  />
                </div>
                <div className={styles.buttonContainer}>
                  <SettingButton isLoading={isSubmitting} disabled={isSubmitting} color='blue' buttonType='submit'>
                    {t('投稿する')}
                  </SettingButton>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    )
  }
)
